'use client';

import { useState } from 'react';
import { CardButton } from '../card-button/card-button';
import { ModalNext, Box, Button, Flex, Stack } from '@instamenu/ui-system-next';
import { scrollableBox, tableList, tableOption, selectedTable } from './table-select.css';
import { Text } from '@instamenu/ui-system-next';
import { themeVars } from '@instamenu/ui-vanilla-extract';
import { Order, useCheckoutContext } from '@instamenu/context-checkout';
import { observer } from 'mobx-react-lite';
import { TableMappingKind } from '@instamenu/data-access';
import { captureClientException } from '@instamenu/posthog/client';

type TableSelectProps = {
  order: Order;
  kind?: TableMappingKind;
};

export const TableSelect = observer(({ order, kind }: TableSelectProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTableCode, setSelectedTableCode] = useState(order.table);
  const checkout = useCheckoutContext();

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string>();
  const mappingKind =
    kind ?? (order.isPickupLocation ? TableMappingKind.PickupLocation : TableMappingKind.DineIn);
  const mappings = order.restaurant.tableMappings.filter((mapping) => mapping.kind === mappingKind);
  const selectLabel =
    mappingKind === TableMappingKind.PickupLocation ? 'Select location' : 'Select table';

  const handleTableSelect = (tableCode: string) => {
    setSelectedTableCode(tableCode);
  };

  const handleClose = () => {
    if (saving) return;
    setIsModalOpen(false);
    setSelectedTableCode(order.table);
  };

  const handleSelectTable = async () => {
    const selection = mappings.find((mapping) => mapping.code === selectedTableCode);
    if (saving || !selection) return;
    setSaving(true);
    setSaveError(undefined);
    try {
      await order.editTable({ table: selection.code });
      if (order.id && checkout) checkout.editLocalStorageTable(order.id, selection.code);
      setIsModalOpen(false);
    } catch (error) {
      captureClientException(error, {
        source: 'ui-storefront.table-select.save',
        properties: {
          orderId: order.id,
          restaurantId: order.restaurant.id,
          tableCode: selection.code,
          tableMappingKind: mappingKind,
        },
      });
      setSaveError(
        error instanceof Error ? error.message : 'Unable to save your selection. Please try again.',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Box data-id="table-select">
        <CardButton
          label={mappings.find((table) => table.code === order.table)?.name || selectLabel}
          onClick={() => {
            setSelectedTableCode(order.table);
            setSaveError(undefined);
            setIsModalOpen(true);
          }}
        />
      </Box>

      <ModalNext
        opened={isModalOpen}
        containerStyle={{ padding: themeVars.pad[12] }}
        onRequestClose={handleClose}
        withCloseButton={!saving}
        closeOnBackgroundClick={!saving}
        data-id="table-select-modal"
      >
        <Stack
          css={{
            flex: 1,
            justifyContent: 'space-between',
          }}
        >
          <Flex
            style={{ alignItems: 'center', justifyContent: 'space-between', paddingBottom: 18 }}
          >
            <Text
              css={{
                flex: 1,
                margin: 0,
                fontSize: 17,
                fontWeight: 500,
                textAlign: 'center',
              }}
            >
              {selectLabel}
            </Text>
          </Flex>
          <Box className={scrollableBox}>
            <div className={tableList}>
              {mappings
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
                .map((table) => (
                  <button
                    key={table.id}
                    data-id="table-select-option"
                    type="button"
                    disabled={saving}
                    className={`${tableOption} ${
                      table.code === selectedTableCode ? selectedTable : ''
                    }`}
                    onClick={() => handleTableSelect(table.code)}
                  >
                    <Text variant="body1" css={{ fontWeight: 500 }}>
                      {table.name}
                    </Text>
                  </button>
                ))}
            </div>
          </Box>
          {saveError && (
            <Text error role="alert">
              {saveError}
            </Text>
          )}
          <Button
            data-id="table-select-save"
            loading={saving}
            variant="primary"
            disabled={
              saving ||
              selectedTableCode === order.table ||
              !mappings.some((mapping) => mapping.code === selectedTableCode)
            }
            onClick={handleSelectTable}
          >
            {selectLabel}
          </Button>
        </Stack>
      </ModalNext>
    </>
  );
});
